import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button, FormHelperText } from '@mui/material';
import { useContext, useRef } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GoogleMapBox } from '../../../../components/basic/GoogleMapBox';
import { GoogleSearchBox } from '../../../../components/basic/GoogleSearchBox';
import { FormFileContent } from '../../../../components/form/FormFileContent';
import { FormFileUploader } from '../../../../components/form/FormFileUploader';
import { FormInput } from '../../../../components/form/FormInput';
import { FormInputLabel } from '../../../../components/form/FormInputLabel';
import { FormRadio } from '../../../../components/form/FormRadio';
import { LangContext } from '../../../../contexts';
import { buttonStyle } from '../../../../styles/style';
import { signUpFormValidations } from './form';

const kycRadioStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  justifyContent: 'flex-start',
  gap: 0,
};

export const SignUpForm = (props) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    onSubmit,
    control,
    setError,
    errors,
    setStep,
    googleAddress,
    setGoogleAddress,
  } = props;
  const { isKorean } = useContext(LangContext);
  const validations = signUpFormValidations(!isKorean);
  const fileRef = useRef(null);
  const businessFile = watch('businessFile');
  const isOtherOwns25 = watch('kyc.isOtherOwns25');
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control,
    name: 'kyc.ownerList',
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
      <section className='flex flex-col w-full gap-4 mb-6 container max-w-[560px] pt-10'>
        <header className='flex items-center border-b border-solid border-gray-500 pb-3 mb-3 justify-between'>
          <h2 className='font-semibold'>{t('LOGIN_INFO')}</h2>
          <em className='flex items-center gap-1 text-red-500'>
            <div className='ico required text-red-500'></div>
            <span className='text-sm text-red-500 font-semibold'>{t('REQUIRED')}</span>
          </em>
        </header>
        <FormInput
          {...register('email', validations.email)}
          label={t('EMAIL')}
          required
          error={errors.email}
          inputProps={{
            autoComplete: 'one-time-code',
          }}
        />
        <FormInput
          {...register('password', validations.password)}
          type='password'
          label={t('PASSWORD')}
          required
          error={errors.password}
          inputProps={{
            autoComplete: 'one-time-code',
          }}
        />
        <FormInput
          {...register('passwordConfirm', {
            ...validations.passwordConfirm,
            validate: (value, formValues) => {
              return validations.passwordConfirm.validate(value, formValues);
            },
          })}
          type='password'
          label={t('PASSWORD_CONFIRM')}
          inputProps={{
            autoComplete: 'one-time-code',
          }}
          required
          error={errors.passwordConfirm}
        />
        <FormInput {...register('name', validations.name)} label={t('RESPONSIBILITY')} required error={errors.name} />
        <FormInput
          {...(() => {
            const { onChange, ...rest } = register('phone', validations.phone);
            return {
              ...rest,
              onChange: (e) => {
                const reg = new RegExp('^[0-9]{0,11}$');
                const value = e.target.value;
                if (reg.test(value)) {
                  onChange(e);
                } else {
                  e.target.value = value.slice(0, -1);
                }
              },
            };
          })()}
          label={t('MOBILE')}
          required
          error={errors.phone}
        />
      </section>
      <section className='flex flex-col w-full gap-4 mb-6 container max-w-[560px] pt-10'>
        <header className='flex items-center border-b border-solid border-gray-500 pb-3 mb-3 justify-between'>
          <h2 className='font-semibold'>{t('COMPANY_PROFILE')}</h2>
          <em className='flex items-center gap-1 text-red-500'>
            <div className='ico required text-red-500'></div>
            <span className='text-sm text-red-500 font-semibold'>{t('REQUIRED')}</span>
          </em>
        </header>
        <div className='flex flex-col gap-4 mt-4'>
          <FormInput
            {...register('companyName', validations.companyName)}
            label={t('COMPANY_NAME')}
            required
            error={errors.companyName}
          />
          <FormInput
            {...register('ceoName', validations.ceoName)}
            label={t('CEO_NAME')}
            required
            error={errors.ceoName}
          />
          <FormInput
            {...(() => {
              const { onChange, ...rest } = register('businessNumber', validations.businessNumber);
              return {
                ...rest,
                onChange: (e) => {
                  const reg = new RegExp('^[0-9]{0,10}$');
                  const value = e.target.value;
                  if (reg.test(value)) {
                    onChange(e);
                  } else {
                    e.target.value = value.slice(0, -1);
                  }
                },
              };
            })()}
            label={t('BIZ_NUMBER')}
            required
            error={errors.businessNumber}
          />
          <FormFileUploader
            name='businessFile'
            control={control}
            label={t('BIZ_FILE')}
            error={errors.businessFile}
            rules={validations.businessFile}
            ref={fileRef}
          >
            <FormFileContent
              file={businessFile}
              name='businessFile'
              label={t('BIZ_CERT')}
              error={errors.businessFile}
              required
              setValue={setValue}
              onClick={() => fileRef.current?.click()}
            />
          </FormFileUploader>
          <div className='flex flex-col gap-1'>
            <Controller
              control={control}
              name='address.addr1'
              rules={validations['address.addr1']}
              error={errors.address?.addr1}
              render={({ field }) => (
                <>
                  <FormInputLabel label={t('HEADQ_ADDRESS')} isRequired />
                  <GoogleSearchBox value={googleAddress} onChange={(value) => setGoogleAddress(value)} />
                  <div className='rounded-lg border overflow-hidden'>
                    <GoogleMapBox initialAddress={googleAddress} onChange={(value) => field.onChange(value)} />
                  </div>
                  {errors.address?.addr1 && (
                    <FormHelperText error={errors.address?.addr1}>{errors.address?.addr1.message}</FormHelperText>
                  )}
                </>
              )}
            />
            <FormInput {...register('address.addr2')} placeholder={t('ADDRESS_DETAIL')} error={errors.address?.addr2} />
          </div>
          <FormInput {...register('site', validations.site)} label={t('HOMEPAGE')} error={errors.site} />
          <FormInput
            {...(() => {
              const { onChange, ...rest } = register('fax', validations.fax);
              return {
                ...rest,
                onChange: (e) => {
                  const reg = new RegExp('^[0-9]{0,11}$');
                  const value = e.target.value;
                  if (reg.test(value)) {
                    onChange(e);
                  } else {
                    e.target.value = value.slice(0, -1);
                  }
                },
              };
            })()}
            label={t('FAX')}
            error={errors.fax}
          />
        </div>
      </section>
      <section className='flex flex-col w-full gap-4 mb-6 container max-w-[560px] pt-10'>
        <header className='flex items-center border-b border-solid border-gray-500 pb-3 mb-3 justify-between'>
          <h2 className='font-semibold'>{t('KYC-CHECK')}</h2>
          <em className='flex items-center gap-1 text-red-500'>
            <div className='ico required text-red-500'></div>
            <span className='text-sm text-red-500 font-semibold'>{t('REQUIRED')}</span>
          </em>
        </header>
        <div className='mt-3 '>
          <p className='text-gray-500' style={{ fontSize: '14px', lineHeight: '18px' }}>
            {t('KYC-DESCRIPTION')}
          </p>
        </div>
        <div className='flex flex-col gap-4 mt-8'>
          <p style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 500 }}>{t('KYC-POSITION-Q')}</p>
          <FormInput
            {...register('kyc.position', validations['kyc.position'])}
            label={t('KYC-POSITION')}
            required
            error={errors.kyc?.position}
          />
          <FormInput
            {...register('kyc.role', validations['kyc.role'])}
            label={t('KYC-ROLE')}
            required
            error={errors.kyc?.role}
          />
          <FormRadio
            control={control}
            name='kyc.isRepOwns25'
            label={t('KYC-ISREPOWNS25')}
            menus={[
              { value: true, label: 'YES-REP-OWNS' },
              { value: false, label: 'NO-REP-OWNS' },
            ]}
            radioGroupSx={kycRadioStyle}
            error={errors.kyc?.isRepOwns25}
          />
          <FormRadio
            control={control}
            name='kyc.isRepOnly25'
            label={t('KYC-ISREPONlY25')}
            menus={[
              { value: true, label: 'YES-REP-ONLY' },
              { value: false, label: 'NO-REP-ONLY' },
            ]}
            radioGroupSx={kycRadioStyle}
            error={errors.kyc?.isRepOnly25}
          />
          <FormRadio
            control={control}
            name='kyc.isOtherOwns25'
            label={t('KYC-ISOTHEROWNS25')}
            menus={[
              { value: true, label: 'YES-OTHER-OWNS' },
              { value: false, label: 'NO-OTHER-OWNS' },
            ]}
            radioGroupSx={kycRadioStyle}
            error={errors.kyc?.isOtherOwns25}
          />
        </div>
      </section>
      <section className='mt-[40px]'>
        {(isOtherOwns25 === true || isOtherOwns25 === 'true') && (
          <>
            <section className='user-form__container !mt-0'>
              <div className='w-full h-[1px] bg-[rgba(0,_0,_0,_0.2)]'></div>
              <ul className='form__container'>
                <p className='mt-[40px] mb-5' style={{ fontSize: '14px', lineHeight: '24px', fontWeight: 500 }}>
                  {t('KYC-POSITION-R')}
                </p>
              </ul>
              <div className='w-full flex flex-col gap-3'>
                {fields.map((field, index) => (
                  <div key={field.id} className='grid grid-cols-2 gap-3'>
                    <FormInput
                      {...register(`kyc.ownerList.${index}.name`)}
                      label={t('KYC-OWNERNAME1')}
                      error={errors.kyc?.ownerList?.[index]?.name}
                    />
                    <div className='flex items-center gap-3'>
                      <FormInput
                        {...register(`kyc.ownerList.${index}.position`)}
                        label={t('KYC-OWNERPOS1')}
                        error={errors.kyc?.ownerList?.[index]?.position}
                      />
                      <Button
                        type='button'
                        sx={{
                          mt: 2,
                        }}
                        onClick={() => remove(index)}
                        color='error'
                      >
                        {t('DELETE')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {fields.length < 4 && (
                <div className='flex items-center justify-center mt-5 mb-8'>
                  <Button
                    type='button'
                    onClick={() => append({ name: '', position: '' })}
                    color='primary'
                    startIcon={<AddRoundedIcon style={{ cursor: 'pointer', color: '#5D38E5' }} />}
                  >
                    {isKorean ? '추가하기' : 'Add'}
                  </Button>
                </div>
              )}
            </section>
          </>
        )}
      </section>
      <section className='flex justify-center gap-6 pt-6'>
        <Button
          sx={{ ...buttonStyle, width: '180px' }}
          disableElevation
          variant='outlined'
          type='button'
          onClick={() => {
            setStep(0);
          }}
        >
          {t('PREV')}
        </Button>
        <Button sx={{ ...buttonStyle, width: '180px' }} disableElevation type='submit' variant='contained'>
          {t('NEXT')}
        </Button>
      </section>
    </form>
  );
};
